/**
 * OutputMirror — the per-binding egress pipeline (spec §7.2): consumes raw
 * AgentEvents from a SessionPort subscription and mirrors them into a chat
 * conversation through a ChannelAdapter, using the vendored streaming modules
 * (throttled serialized edits, markdown auto-close, frozen-boundary chunking).
 */
import type { AgentEvent, UserMessageEvent } from "@skaile/workspaces/types";
import { ulid } from "ulid";
import type { ChannelAdapter, ChannelLogger } from "../adapter.js";
import type { Binding, MessageRef, OutboundMessage, ReplyTarget } from "../types.js";
import { autoCloseMarkdown } from "./vendor/auto-close-streaming.js";
import { ChunkedMessageStream } from "./vendor/chunked-message-stream.js";
import { SerializedMessageStream } from "./vendor/message-stream.js";

/** Minimum interval between native typing-indicator calls (Decision 9). */
const TYPING_THROTTLE_MS = 3_000;

/** Options for {@link OutputMirror}. */
export type OutputMirrorOptions = {
  adapter: ChannelAdapter;
  binding: Binding;
  target: ReplyTarget;
  /** Egress dedupe key prefix — engine passes `${sessionId}`. */
  dedupeScope: string;
  /** Called with every MessageRef this mirror posts (engine records ReplyRef↔MessageRef + loop guard). */
  onPosted?: (ref: MessageRef, turnMessageId: string) => void;
  /** Turn-origin oracle for mirrorMode "chat-initiated" (engine-owned state). */
  isChatInitiatedTurn?: () => boolean;
  logger: ChannelLogger;
};

/** Streaming state for one agent turn: buffer, edit queue, chunker, posted refs. */
type Turn = {
  /** ULID minted per turn — the `messageId` slot of the egress dedupe key. */
  id: string;
  buffer: string;
  /** Set on `finished` so the final flush skips auto-close padding. */
  finishing: boolean;
  stream: SerializedMessageStream;
  chunks: ChunkedMessageStream;
  /** MessageRef per chunk index, filled as chunks are posted. */
  refs: MessageRef[];
};

/**
 * The egress pipeline for one (binding, conversation) pair.
 *
 * `text` events accumulate into a per-turn buffer that streams to the platform
 * as one growing message (post-then-update), auto-closing unbalanced markdown
 * mid-stream and chunking at `capabilities.maxMessageLength` with frozen
 * boundaries. `question` / `error` / mirrored `user_message`s post as their
 * own messages. Every egress post carries an idempotency key
 * `${dedupeScope}:${messageId}:${chunkIndex}` and is retried once on failure
 * with the SAME key (spec §8 at-least-once discipline).
 *
 * Invariant: `handleEvent` is synchronous — all platform I/O runs on internal
 * queues; `dispose()` flushes them.
 */
export class OutputMirror {
  private readonly opts: OutputMirrorOptions;
  private turn: Turn | undefined;
  /** Serializes side-posts and turn finalization; awaited by dispose(). */
  private tail: Promise<void> = Promise.resolve();
  private lastTypingAt = Number.NEGATIVE_INFINITY;
  private disposed = false;

  constructor(opts: OutputMirrorOptions) {
    this.opts = opts;
  }

  /** Wire as the SessionPort.subscribe sink. Synchronous fan-in; work is internal. */
  handleEvent(e: AgentEvent): void {
    if (this.disposed) return;
    switch (e.type) {
      case "text":
        this.onText(e.content);
        break;
      case "finished":
        this.finishTurn();
        break;
      case "question":
        this.postAside({ markdown: e.question, kind: "question", options: e.options });
        break;
      case "error":
        this.postAside({ markdown: e.message, kind: "error" });
        break;
      case "typing":
        this.onTyping(e.isTyping);
        break;
      case "user_message":
        this.onUserMessage(e);
        break;
      default:
        break;
    }
  }

  /** Flush pending state and stop. */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.finishTurn();
    this.disposed = true;
    await this.tail;
  }

  private onText(content: string): void {
    if (!content) return;
    this.turn ??= this.newTurn();
    this.turn.buffer += content;
    this.turn.stream.enqueue(this.turn.buffer);
  }

  /** Detach the current turn, drain it on the tail queue, and start fresh. */
  private finishTurn(): void {
    const turn = this.turn;
    this.turn = undefined;
    if (!turn) return;
    turn.finishing = true;
    this.enqueue(async () => {
      await turn.stream.flush();
      await turn.chunks.finalize();
    });
  }

  private newTurn(): Turn {
    const { adapter, dedupeScope, logger, onPosted } = this.opts;
    const id = ulid();
    const refs: MessageRef[] = [];
    const onApplyError = (err: unknown) => logger.error("egress apply failed", err);
    const chunks = new ChunkedMessageStream({
      maxLength: adapter.capabilities.maxMessageLength,
      // The outer stream already paces writes at editThrottleMs.
      throttleMs: 0,
      onApplyError,
      post: async (chunk, index) => {
        const ref = await this.postWithRetry({ markdown: chunk }, `${dedupeScope}:${id}:${index}`);
        refs[index] = ref;
        onPosted?.(ref, id);
      },
      update: async (chunk, index) => {
        const ref = refs[index];
        if (!ref) {
          logger.warn("skipping update for chunk that never posted", { index });
          return;
        }
        await adapter.update(ref, { markdown: chunk });
      },
    });
    const turn: Turn = {
      id,
      buffer: "",
      finishing: false,
      refs,
      chunks,
      stream: new SerializedMessageStream({
        throttleMs: adapter.capabilities.editThrottleMs,
        onApplyError,
        // Mid-stream, balance the unfinished markdown; the final flush sends
        // the turn's exact text. Caveat: ChunkedMessageStream still auto-closes
        // per chunk, so a turn ENDING unbalanced is padded closed anyway.
        apply: (content) => chunks.write(turn.finishing ? content : autoCloseMarkdown(content)),
      }),
    };
    return turn;
  }

  /** Post a standalone message (question / error / mirrored user text). */
  private postAside(msg: OutboundMessage): void {
    const messageId = ulid();
    this.enqueue(async () => {
      const ref = await this.postWithRetry(msg, `${this.opts.dedupeScope}:${messageId}:0`);
      this.opts.onPosted?.(ref, messageId);
    });
  }

  /** One retry with the SAME dedupe key — the adapter's idempotency absorbs duplicates. */
  private async postWithRetry(msg: OutboundMessage, dedupeKey: string): Promise<MessageRef> {
    const { adapter, target, logger } = this.opts;
    try {
      return await adapter.post(target, msg, dedupeKey);
    } catch (err) {
      logger.warn("post failed; retrying once with the same dedupe key", {
        dedupeKey,
        error: String(err),
      });
      return adapter.post(target, msg, dedupeKey);
    }
  }

  private onTyping(isTyping: boolean): void {
    const { adapter, target, logger } = this.opts;
    if (!isTyping || !adapter.capabilities.supportsTyping || !adapter.indicateTyping) return;
    const now = Date.now();
    if (now - this.lastTypingAt < TYPING_THROTTLE_MS) return;
    this.lastTypingAt = now;
    adapter.indicateTyping(target).catch((err) => logger.error("indicateTyping failed", err));
  }

  /** Mirror-mode + visibility gating for user_message events (spec §7.2.4, §5). */
  private onUserMessage(e: UserMessageEvent): void {
    const { binding, isChatInitiatedTurn } = this.opts;
    if (binding.mirrorMode === "agent-only") return;
    if (e.visibilityMode === "Private" || e.visibilityMode === "HumansOnly") return;
    if (e.privateRecipientIds && e.privateRecipientIds.length > 0) return;
    if (binding.mirrorMode === "chat-initiated" && isChatInitiatedTurn?.() !== true) return;
    this.postAside({ markdown: `**${e.senderName}:** ${e.content}` });
  }

  /** Append work to the tail queue; failures log rather than poison the chain. */
  private enqueue(task: () => Promise<void>): void {
    this.tail = this.tail
      .then(task)
      .catch((err) => this.opts.logger.error("egress task failed", err));
  }
}
